/**
 * №16.1 Stage C — three of the four locked editors (16.1-authoring-editors-v1):
 *   1. EffectsEditor  — "WHEN THIS HAPPENS" (r12: WRITE-only, event-bound, re-pick safe)
 *   2. ConditionsEditor — "APPLIES WHEN" (r14: applicability, decided once at open)
 *   3. SignalsEditor  — "WAITING ON THE OUTSIDE WORLD" (W40: instance-scoped default)
 * (the 4th, derive review, is a view — DeriveReview.tsx). Each emits patch batches
 * through the one door; typed pickers only, no raw JSON.
 *
 * №16.1.4 — humanisation: every enumerable value is PICKED, not typed. Attributes
 * are pickers over the subject entity's declared fields (the D-RS6 registry);
 * values type themselves by field type; assignment-flavoured effects pick users
 * (workspace members) and their role slots from the served catalog descriptor —
 * never a typed code, never a hardcoded option list.
 */
import { useState } from "react";
import type { ConditionLeaf, EffectView, WfPatch, ActionCatalog } from "../../hooks/use-workflow-templates";
import { effectLabel, effectWhen } from "./wf-author-shared";
import { AttributeSelect, TypedValueInput } from "./FieldPickers";
import { fieldLabel } from "./wf-fields-shared";
import { MemberPicker } from "../MemberPicker";

type Patch = (patches: WfPatch[]) => void;

// ── shared ─────────────────────────────────────────────────────────────────
const EVENT_FOR_OWNER: Record<string, string[]> = {
  segment: ["onOpen", "onComplete"], step: ["onComplete"], decision: ["onDecide"], outcome: ["onDecide"], workflow: ["onStart", "onComplete"],
};
const EVENT_LABEL: Record<string, string> = {
  onOpen: "when it opens", onComplete: "when it completes", onDecide: "when decided", onStart: "when the workflow starts",
};

/** Options from a served catalog descriptor like "a|b|c" — never a hardcoded list
 *  (R5). Returns [] when the descriptor is a type hint rather than a fixed enum. */
function pipeOpts(desc?: string): string[] {
  if (!desc) return [];
  const parts = desc.split("|").map((s) => s.trim());
  return parts.length >= 2 && parts.every((p) => p && !/[()?]|string|number|text/i.test(p)) ? parts : [];
}

// ── 1. EFFECTS ─────────────────────────────────────────────────────────────
const VERB_LABEL: Record<string, string> = {
  setMatterDate: "Set a key date", setAttribute: "Set a field", setMatterField: "Assign a matter role", assign: "Assign someone",
};

export function EffectsEditor({ effects, ownerKind, ownerNo, outcomeCode, catalog, subjectType, patch, busy }: {
  effects: EffectView[]; ownerKind: "segment" | "decision" | "outcome" | "step" | "workflow"; ownerNo: number; outcomeCode?: string;
  catalog: ActionCatalog | undefined; subjectType: string | undefined; patch: Patch; busy: boolean;
}) {
  const [adding, setAdding] = useState(false);
  const verbs = Object.keys(catalog?.effectVerbs ?? VERB_LABEL);
  const [verb, setVerb] = useState(verbs[0] ?? "setAttribute");
  const [event, setEvent] = useState(EVENT_FOR_OWNER[ownerKind]?.[0] ?? "onComplete");
  const [params, setParams] = useState<Record<string, string>>({});
  const mine = effects.filter((e) => e.ownerKind === ownerKind && (outcomeCode ? e.outcomeCode === outcomeCode : true));
  const evLabels = catalog?.effectVerbs ?? {};

  const reset = () => { setParams({}); setAdding(false); };
  const add = () => {
    const p: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(params)) if (v) p[k] = v;
    patch([{ op: "addEffect", ownerKind, ownerNo, event, verb, ...(outcomeCode ? { outcomeCode } : {}), params: p }]);
    reset();
  };

  return (
    <div>
      <div className="wfa-k">WHEN THIS HAPPENS</div>
      {mine.length === 0 && !adding && <div className="wfa-fx" style={{ borderStyle: "dashed", color: "var(--color-text-secondary)" }}><span className="set">no effects yet</span></div>}
      {mine.map((e) => (
        <div key={e.effectNo} className="wfa-fx">
          <span className="when">{effectWhen(e)}</span><span className="arrow">→</span>
          <span className="set">{effectLabel(e)}</span>
          <button className="del" title="remove" disabled={busy} onClick={() => patch([{ op: "removeEffect", effectNo: e.effectNo }])}>✕</button>
        </div>
      ))}
      {adding ? (
        <div className="wfa-trigbox" style={{ marginTop: 8 }}>
          <div className="wfa-fld"><label>When</label>
            <select value={event} onChange={(e) => setEvent(e.target.value)} style={{ width: "100%" }}>{(EVENT_FOR_OWNER[ownerKind] ?? []).map((ev) => <option key={ev} value={ev}>{EVENT_LABEL[ev] ?? ev}</option>)}</select>
          </div>
          <div className="wfa-fld"><label>Do this</label>
            <select value={verb} onChange={(e) => { setVerb(e.target.value); setParams({}); }} style={{ width: "100%" }}>{verbs.map((v) => <option key={v} value={v}>{VERB_LABEL[v] ?? fieldLabel(v)}</option>)}</select>
          </div>
          <EffectParams verb={verb} subjectType={subjectType} evLabels={evLabels} params={params} setParams={setParams} busy={busy} />
          <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
            <button className="wfa-btn primary" disabled={busy} onClick={add}>Add effect</button>
            <button className="del" onClick={reset}>cancel</button>
          </div>
        </div>
      ) : (
        <button className="wfa-add" onClick={() => setAdding(true)}>+ Add effect · set a field when this fires</button>
      )}
      <div className="wfa-note"><b>✎ write-only</b> — effects set values; they never branch, wait, or invoke (that's a step). Re-choosing a decision safely overwrites.</div>
    </div>
  );
}

/** The typed param form for one effect verb — pickers only, options from the
 *  registry (subject fields) or the served descriptor (role slots). */
function EffectParams({ verb, subjectType, evLabels, params, setParams, busy }: {
  verb: string; subjectType: string | undefined; evLabels: Record<string, Record<string, string>>;
  params: Record<string, string>; setParams: (p: Record<string, string>) => void; busy: boolean;
}) {
  const set = (k: string, v: string) => setParams({ ...params, [k]: v });
  if (verb === "setMatterDate") return (
    <>
      <div className="wfa-fld"><label>Date field</label><AttributeSelect subjectType={subjectType} dateOnly value={params.dateType ?? ""} onChange={(v) => set("dateType", v)} busy={busy} /></div>
      <div className="wfa-fld"><label>Date (blank = decision date)</label><input type="date" value={params.date ?? ""} disabled={busy} onChange={(e) => set("date", e.target.value)} /></div>
    </>
  );
  if (verb === "setAttribute") return (
    <>
      <div className="wfa-fld"><label>Field</label><AttributeSelect subjectType={subjectType} value={params.attribute ?? ""} onChange={(v) => setParams({ ...params, attribute: v, value: "" })} busy={busy} /></div>
      <div className="wfa-fld"><label>Value</label><TypedValueInput subjectType={subjectType} fieldName={params.attribute ?? ""} value={params.value ?? ""} onChange={(v) => set("value", v)} busy={busy} /></div>
    </>
  );
  if (verb === "setMatterField") {
    const opts = pipeOpts(evLabels.setMatterField?.field);
    return (
      <>
        <div className="wfa-fld"><label>Matter role</label>
          <select value={params.field ?? ""} disabled={busy} onChange={(e) => set("field", e.target.value)} style={{ width: "100%" }}>
            <option value="">— pick —</option>{opts.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>
        <div className="wfa-fld"><label>Person</label><MemberPicker value={params.value || null} onChange={(id) => set("value", id ?? "")} allowAgents={false} placeholder="Pick a person" /></div>
      </>
    );
  }
  if (verb === "assign") {
    const roleOpts = pipeOpts(evLabels.assign?.role);
    return (
      <>
        <div className="wfa-fld"><label>Role</label>
          <select value={params.role ?? ""} disabled={busy} onChange={(e) => setParams({ ...params, role: e.target.value, person: "" })} style={{ width: "100%" }}>
            <option value="">— pick —</option>{roleOpts.map((o) => <option key={o} value={o}>{fieldLabel(o)}</option>)}
          </select>
        </div>
        {params.role === "person" && <div className="wfa-fld"><label>Person</label><MemberPicker value={params.person || null} onChange={(id) => set("person", id ?? "")} allowAgents={false} placeholder="Pick a person" /></div>}
      </>
    );
  }
  // A verb the editor can't yet express — honest, never silent (№16.1.4 B).
  return <div className="wfa-note">This effect isn't editable here yet — use the command strip to set its details.</div>;
}

// ── 2. CONDITIONS ────────────────────────────────────────────────────────
const SOURCE_LABEL: Record<string, string> = {
  matterField: "A matter field", matterDate: "A key date", attribute: "A reporting attribute", role: "A role", decision: "A decision", context: "A carried value",
};
export function ConditionsEditor({ conditions, ownerKind, ownerNo, logic, catalog, subjectType, patch, busy }: {
  conditions: ConditionLeaf[]; ownerKind: "segment" | "step"; ownerNo: number; logic: string;
  catalog: ActionCatalog | undefined; subjectType: string | undefined; patch: Patch; busy: boolean;
}) {
  const [adding, setAdding] = useState(false);
  const sources = catalog?.conditionSources ?? ["matterField", "matterDate", "attribute", "role", "decision", "context"];
  const operators = catalog?.conditionOperators ?? ["eq", "neq", "isSet", "isNotSet", "gt", "gte", "lt", "lte"];
  // D1 (r14) — operators are TYPE-DRIVEN by the source: relational ops (gt/lt…) only make sense on a
  // date; membership/role sources are equality/presence only. Changing the source resets the field +
  // operator to the first valid one, so a meaningless source·operator pair can never be authored.
  const only = (...keep: string[]) => operators.filter((o) => keep.includes(o));
  const OPS_BY_SOURCE: Record<string, string[]> = {
    matterDate: only("eq", "neq", "gt", "gte", "lt", "lte", "isSet", "isNotSet"),
    role: only("eq", "neq", "isSet", "isNotSet"),
    decision: only("eq", "neq", "isSet", "isNotSet"),
    context: only("eq", "neq", "isSet", "isNotSet"),
  };
  const opsFor = (src: string): string[] => { const o = OPS_BY_SOURCE[src]; return o && o.length ? o : operators; };
  const firstOp = (src: string): string => opsFor(src)[0] ?? "eq";
  const [draft, setDraft] = useState({ source: sources[0] ?? "matterField", field: "", op: firstOp(sources[0] ?? "matterField"), value: "" });
  // A source that reads a declared entity field → the field is PICKED, and the value types itself.
  const fieldIsEntity = (src: string) => src === "matterField" || src === "attribute" || src === "matterDate";
  const OP_WORD: Record<string, string> = { eq: "is", neq: "is not", isSet: "is set", isNotSet: "is not set", gt: "after", gte: "on or after", lt: "before", lte: "on or before" };

  const emit = (leaves: { source: string; field: string; op: string; value?: string | null; valueType?: string }[]) => {
    if (leaves.length === 0) patch([{ op: "clearCondition", ownerKind, ownerNo }]);
    else patch([{ op: "setCondition", ownerKind, ownerNo, logic: logic || "all", leaves }]);
  };
  const remove = (leafNo: number) => emit(conditions.filter((c) => c.leafNo !== leafNo).map((c) => ({ source: c.source, field: c.field, op: c.op, value: c.value, valueType: c.valueType })));
  const add = () => {
    if (!draft.field) return;
    emit([...conditions.map((c) => ({ source: c.source, field: c.field, op: c.op, value: c.value, valueType: c.valueType })), { source: draft.source, field: draft.field, op: draft.op, value: draft.value }]);
    setDraft({ source: sources[0] ?? "matterField", field: "", op: firstOp(sources[0] ?? "matterField"), value: "" }); setAdding(false);
  };
  const noValue = (op: string) => op === "isSet" || op === "isNotSet";

  return (
    <div>
      <div className="wfa-k">APPLIES WHEN</div>
      {conditions.length === 0 && !adding && <div className="wfa-note" style={{ marginTop: 0 }}>Always applies (no conditions).</div>}
      {conditions.map((c) => (
        <div key={c.leafNo} className="wfa-cond">
          <span className="subj">{fieldLabel(c.field)}</span><span className="op">{OP_WORD[c.op] ?? c.op}</span>{!noValue(c.op) && <span className="val">{c.value}</span>}
          <button className="del" style={{ marginLeft: "auto" }} disabled={busy} onClick={() => remove(c.leafNo)}>✕</button>
        </div>
      ))}
      {adding ? (
        <div className="wfa-trigbox" style={{ marginTop: 8 }}>
          <div className="wfa-fld"><label>Look at</label>
            <select value={draft.source} onChange={(e) => setDraft((d) => ({ ...d, source: e.target.value, op: firstOp(e.target.value), field: "", value: "" }))} style={{ width: "100%" }}>{sources.map((s) => <option key={s} value={s}>{SOURCE_LABEL[s] ?? fieldLabel(s)}</option>)}</select>
          </div>
          <div className="wfa-fld"><label>Field</label>
            {fieldIsEntity(draft.source)
              ? <AttributeSelect subjectType={subjectType} dateOnly={draft.source === "matterDate"} value={draft.field} onChange={(v) => setDraft((d) => ({ ...d, field: v, value: "" }))} busy={busy} />
              : <input placeholder="name" value={draft.field} onChange={(e) => setDraft((d) => ({ ...d, field: e.target.value }))} style={{ width: "100%" }} />}
          </div>
          <div className="wfa-fld"><label>Test</label>
            <select value={draft.op} onChange={(e) => setDraft((d) => ({ ...d, op: e.target.value }))} style={{ width: "100%" }}>{opsFor(draft.source).map((o) => <option key={o} value={o}>{OP_WORD[o] ?? o}</option>)}</select>
          </div>
          {!noValue(draft.op) && (
            <div className="wfa-fld"><label>Value</label>
              {fieldIsEntity(draft.source) && draft.source !== "matterDate"
                ? <TypedValueInput subjectType={subjectType} fieldName={draft.field} value={draft.value} onChange={(v) => setDraft((d) => ({ ...d, value: v }))} busy={busy} />
                : draft.source === "matterDate"
                  ? <input type="date" value={draft.value} onChange={(e) => setDraft((d) => ({ ...d, value: e.target.value }))} />
                  : <input placeholder="value" value={draft.value} onChange={(e) => setDraft((d) => ({ ...d, value: e.target.value }))} style={{ width: "100%" }} />}
            </div>
          )}
          <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
            <button className="wfa-btn primary" disabled={busy || !draft.field} onClick={add}>Add condition</button>
            <button className="del" onClick={() => setAdding(false)}>cancel</button>
          </div>
        </div>
      ) : (
        <button className="wfa-add" onClick={() => setAdding(true)}>+ Add condition · field · is / is not · value</button>
      )}
      <div className="wfa-evalnote"><span>⏱</span>Checked ONCE, when this would open — with the values on the instance. Conditions decide <b>whether</b> work applies, never <b>when</b> it starts.</div>
    </div>
  );
}

// ── 3. SIGNALS (the completion binding — a step's wait) ─────────────────────
export function SignalsEditor({ stepNo, binding, patch, busy }: {
  stepNo: number; binding: { kind: string; eventKey: string; filters: unknown } | null; patch: Patch; busy: boolean;
}) {
  const [eventKey, setEventKey] = useState(binding?.eventKey ?? "");
  const firmWide = !!binding?.filters && Object.keys((binding.filters as object) ?? {}).length > 0;
  const setBinding = (b: { kind: string; eventKey?: string; filters?: Record<string, unknown> } | null) =>
    patch([{ op: "updateStep", stepNo, binding: b }]);
  const save = (asFirmWide: boolean) => {
    if (!eventKey.trim()) { setBinding(null); return; } // no key → attestation-only wait
    setBinding({ kind: "signal", eventKey: eventKey.trim(), ...(asFirmWide ? { filters: {} } : {}) });
  };
  return (
    <div>
      <div className="wfa-k">WAITS FOR</div>
      {/* eventKey is intentionally free (custom keys allowed — an integration names its
          own signal); there is no declared-signal registry to pick from (№16.1.4 C).
          It is a name the outside world sends, not an enumerable Kuvio value. */}
      <div className="wfa-fld"><label>EVENT KEY</label>
        <input className="wfa-keychip" style={{ width: "100%" }} value={eventKey} onChange={(e) => setEventKey(e.target.value)} onBlur={() => save(firmWide)} placeholder="conv.contract.received" />
      </div>
      <div className="wfa-note" style={{ marginTop: 2 }}>the signal name the outside world sends · custom keys allowed · blank = a person marks it received</div>
      <div className="wfa-k" style={{ marginTop: 10 }}>WHO CAN RELEASE IT</div>
      <div className="wfa-scopebox">
        <button className={`wfa-scopeopt ${!firmWide ? "on" : ""}`} disabled={busy || !eventKey.trim()} onClick={() => save(false)}><b>This matter only</b><span>addressed to this instance — the default, always safe</span></button>
        <button className={`wfa-scopeopt ${firmWide ? "on" : ""}`} disabled={busy || !eventKey.trim()} onClick={() => save(true)}><b>Firm-wide key + filters</b><span>matches on declared filters — for integrations that can't address an instance</span></button>
      </div>
      <div className="wfa-note"><b>Always human-completable:</b> every wait carries <b>Mark received</b> — if the fact arrives by email, anyone (or an agent) satisfies it.</div>
    </div>
  );
}
